import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { DateTime } from 'luxon';
import { TrackedAsyncData } from 'ember-async-data';
import t from 'ember-intl/helpers/t';
import ToggleYesno from './toggle-yesno';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import set from 'ember-set-helper/helpers/set';
import { not } from 'ember-truth-helpers';
import toggle from '../helpers/toggle';
import IliosCalendarWeek from './ilios-calendar-week';
import LoadingSpinner from './loading-spinner';
import Event from '../classes/event';
import { uniqueBy } from '../utils/array-helpers';

export default class OfferingCalendarComponent extends Component {
  @service localeDays;

  @tracked showLearnerGroupEvents = true;
  @tracked showSessionEvents = true;
  @tracked learnerGroupEvents = [];
  @tracked sessionEvents = [];
  @tracked currentEvent = null;

  @cached
  get calendarEventsData() {
    return new TrackedAsyncData(
      this.loadData(
        this.args.startDate,
        this.args.endDate,
        this.args.learnerGroups,
        this.args.session,
      ),
    );
  }

  async loadData(startDate, endDate, learnerGroups, session) {
    if (!learnerGroups) {
      this.learnerGroupEvents = [];
    } else {
      const data = await Promise.all(
        learnerGroups.map(async (learnerGroup) => {
          const offerings = await learnerGroup.offerings;
          return await Promise.all(
            offerings.map(async (offering) => {
              const session = await offering.session;
              const sessionType = await session.sessionType;
              const course = await session.course;
              const school = await course.school;
              return new Event(
                {
                  startDate: DateTime.fromJSDate(offering.startDate).toISO(),
                  endDate: DateTime.fromJSDate(offering.endDate).toISO(),
                  courseTitle: course.title,
                  school: school.id,
                  name: session.title,
                  offering: offering.id,
                  location: offering.location,
                  color: sessionType.calendarColor,
                  postrequisites: [],
                  prerequisites: [],
                },
                false,
                true,
              );
            }),
          );
        }),
      );

      this.learnerGroupEvents = data.reduce((flattened, obj) => {
        return [...flattened, ...obj];
      }, []);
    }

    if (!session) {
      this.sessionEvents = [];
      this.currentEvent = null;
    } else {
      const offerings = await session.offerings;
      const sessionType = await session.sessionType;
      const course = await session.course;
      const school = await course.school;
      this.sessionEvents = await Promise.all(
        offerings.map(async (offering) => {
          return new Event(
            {
              startDate: DateTime.fromJSDate(offering.startDate).toISO(),
              endDate: DateTime.fromJSDate(offering.endDate).toISO(),
              courseTitle: course.title,
              school: school.id,
              name: session.title,
              offering: offering.id,
              location: offering.location,
              color: sessionType.calendarColor,
              postrequisites: [],
              prerequisites: [],
            },
            false,
            true,
          );
        }),
      );

      this.currentEvent = new Event(
        {
          startDate: DateTime.fromJSDate(startDate).toISO(),
          endDate: DateTime.fromJSDate(endDate).toISO(),
          courseTitle: course.title,
          school: school.id,
          name: session.title,
          isPublished: session.isPublished,
          offering: 1,
          location: '',
          color: sessionType.calendarColor,
          postrequisites: [],
          prerequisites: [],
        },
        false,
        false,
      );
    }
  }

  get fromTimeStamp() {
    return DateTime.fromJSDate(this.localeDays.firstDayOfDateWeek(this.args.startDate))
      .set({ hour: 0, minute: 0, second: 0 })
      .toUnixInteger();
  }

  get toTimeStamp() {
    return DateTime.fromJSDate(this.localeDays.lastDayOfDateWeek(this.args.startDate))
      .set({ hour: 23, minute: 59, second: 59 })
      .toUnixInteger();
  }

  get calendarEvents() {
    if (!this.currentEvent) {
      return [];
    }

    let events = [];

    if (this.showLearnerGroupEvents) {
      events = [...events, ...this.learnerGroupEvents];
    }
    if (this.showSessionEvents) {
      events = [...events, ...this.sessionEvents];
    }

    // dedupe events on offering id.
    events = uniqueBy(events, 'offering');

    const currentEventIdentifier =
      this.currentEvent.name + this.currentEvent.startDate + this.currentEvent.endDate;

    const filteredEvents = events.filter((event) => {
      if (!event) {
        return false;
      }
      const eventIdentifier = event.name + event.startDate + event.endDate;
      return eventIdentifier !== currentEventIdentifier;
    });

    return [...filteredEvents, this.currentEvent].filter((event) => {
      const ts = DateTime.fromISO(event.startDate).toUnixInteger();
      return this.fromTimeStamp <= ts && ts <= this.toTimeStamp;
    });
  }
  <template>
    <div class="offering-calendar" data-test-offering-calendar>
      {{#if this.calendarEventsData.isResolved}}
        <h2 class="offering-calendar-title" data-test-offering-calendar-title>
          {{t "general.calendar"}}
        </h2>
        <p class="offering-calendar-filter-options" data-test-filters>
          <span class="filter" data-test-learner-group-events-filter>
            <ToggleYesno
              @yes={{this.showLearnerGroupEvents}}
              @toggle={{fn (mut this.showLearnerGroupEvents)}}
            />
            <label
              {{on "click" (set this "showLearnerGroupEvents" (not this.showLearnerGroupEvents))}}
            >
              {{t "general.showLearnerGroupEvents"}}
            </label>
          </span>
          <span class="filter" data-test-session-events-filter>
            <ToggleYesno
              @yes={{this.showSessionEvents}}
              @toggle={{fn (mut this.showSessionEvents)}}
            />
            <label {{on "click" (toggle "showSessionEvents" this)}}>
              {{! template-lint-disable no-triple-curlies }}
              {{{t "general.showSessionEvents" sessionTitle=@session.title}}}
            </label>
          </span>
        </p>
        <div class="ilios-calendar">
          <IliosCalendarWeek
            @calendarEvents={{this.calendarEvents}}
            @date={{@startDate}}
            @areEventsSelectable={{false}}
            @areDaysSelectable={{false}}
          />
        </div>
      {{else}}
        <span class="loading-indicator">
          <LoadingSpinner />
          {{t "general.loadingEvents"}}
          ...
        </span>
      {{/if}}
    </div>
  </template>
}
